import React, { FC, ReactNode } from 'react';
import { FileType } from 'src/types/file';
import Droppzone, { OnUploadType } from './Droppzone';
import base64 from 'base-64';
import DisplayFiles from './DisplayFiles';
import { styled } from '@mui/system';
import { Box } from '@mui/material';
import { colors, sizes } from 'src/themes/default';
import Typography from '../Typography';

export type CustomFileActions = (file: FileType) => ReactNode;

export type FilesPropType = FileType[];

export interface FileUploadPropsType {
    files?: FilesPropType;
    accept?: string;
    label?: string;
    height?: number;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    multiple?: boolean;
    image?: boolean;
    imageCircle?: boolean;
    rawFiles?: boolean;
    displayFilesList?: boolean;
    disableShowFile?: boolean;
    disableDowloadFile?: boolean;
    onChange?: (value: FilesPropType, error: boolean) => void;
    onUpdate?: (value: FilesPropType, error: boolean) => void;
    onDelete?: (value: FileType) => void;
    customFileActions?: CustomFileActions;
    name?: string;
    'data-testid'?: string;
}

const FileInput = styled('div')(({ theme }) => ({
    marginBottom: sizes.file_input_MB,
    display: 'flex',

    [theme.breakpoints.down('sm')]: {
        display: 'block',
    },

    '& .MuiTextField-root': {
        paddingTop: 0,
        paddingBottom: 0,
        display: 'inline-block',
        width: '160px',
        verticalAlign: 'middle',

        '& .MuiSelect-select': {
            padding: '6px 16px 6px 16px',
        },

        '& .MuiFormHelperText-root': {
            display: 'none',
        },

        '& label': {
            top: '-3px',
            background: colors.background_W,
            left: '7px',
            padding: '0px 5px 0px 0px',
            height: '10px',
            lineHeight: '10px',
            verticalAlign: 'middle',
        },
    },
}));

// todo: require dont return error
const FileUpload: FC<FileUploadPropsType> = (props) => {
    const onUpload: OnUploadType = async (newFiles: File[]) => {
        const odlFiles = props.multiple ? props.files || [] : [];
        if (props.disabled || !(props.onChange || props.onUpdate)) {
            return;
        }
        const filteredNewFiles = newFiles.filter(
            (file: File): boolean =>
                !props.files || !props.files.filter((oldFile: FileType): boolean => oldFile.name === file.name).length
        );

        if (props.rawFiles) {
            if (props.onUpdate) {
                props.onUpdate(filteredNewFiles, false);
            }
            if (props.onChange) {
                props.onChange([...odlFiles, ...filteredNewFiles], false);
            }
        } else {
            const newFilesData = await Promise.all(
                filteredNewFiles.map(
                    async (file: File): Promise<FileType> => {
                        return new Promise((callback: any) => {
                            const reader = new FileReader();
                            reader.readAsDataURL(file);
                            reader.onload = () => {
                                callback({
                                    data:
                                        typeof reader.result === 'string'
                                            ? base64.decode(base64.encode(reader.result))
                                            : '',
                                    fileSize: String(file.size || ''),
                                    fileType: file.type,
                                    fileName: file.name,
                                    name: file.name,
                                });
                            };
                        });
                    }
                )
            );

            if (props.onUpdate) {
                props.onUpdate(newFilesData, false);
            }
            if (props.onChange) {
                props.onChange([...odlFiles, ...newFilesData], false);
            }
        }
    };

    const onDeleteFile = (file: FileType) => {
        if (props.files) {
            if (props.onDelete) {
                props.onDelete(file);
            }
            if (props.onChange) {
                props.onChange(
                    props.files.filter((oldFile: FileType) => oldFile !== file),
                    false
                );
            }
        }
    };

    const onDeleteImage = () => {
        if (props.onChange) {
            props.onChange([], false);
        }
    };

    const height = props.height || 100;
    return (
        <Box data-testid={props['data-testid']}>
            {props.label && (
                <Box data-testid={props['data-testid'] ? `${props['data-testid']}-label` : undefined}>
                    <Typography variant="h2" gutterBottom sx={props.error ? { color: colors.alert_O } : {}}>
                        {props.label}
                        {props.required && <span style={{ color: colors.primary_O, marginLeft: '5px' }}>*</span>}
                    </Typography>
                </Box>
            )}
            <FileInput>
                <Box className="file-upload-dropzone" sx={{ flexShrink: 0 }}>
                    <Droppzone
                        accept={props.accept}
                        multiple={props.multiple}
                        image={props.image}
                        imageCircle={props.imageCircle}
                        onUpload={onUpload}
                        height={height}
                        displayFilesList={props.displayFilesList}
                        imageFile={(props.image && props.files && props.files[0]) || undefined}
                        onDeleteImage={onDeleteImage}
                        required={props.required}
                        name={props.name}
                        files={props.files}
                        disabled={props.disabled}
                    />
                </Box>
                {props.displayFilesList && (
                    <DisplayFiles
                        files={props.files}
                        height={height}
                        onDeleteFile={onDeleteFile}
                        disableShowFile={props.disableShowFile}
                        disableDowloadFile={props.disableDowloadFile}
                        customFileActions={props.customFileActions}
                    />
                )}
            </FileInput>
        </Box>
    );
};

export default FileUpload;
